import { useQuery } from "@tanstack/react-query";
import { Megaphone, X } from "lucide-react";
import { useEffect, useState } from "react";

import { api } from "../api/client";
import type { LicenceMessage } from "../api/types";
import { Disclosure } from "../components/panel";
import { useLastDefined } from "../components/presence";
import { IconButton } from "../components/ui";
import { lang, t } from "../i18n/i18n";
import { formatDateTime, LICENCE_QUERY } from "./licence";

/** Message de BELLICOM à une licence (administration des licences, 02/11/2026) : bandeau sous l'annonce d'une
 *  maintenance, montré à tous les postes de la licence, comme elle (ouverture en hauteur et en fondu, croix qui le
 *  masque pour la session). Texte dans la langue de l'interface (le français si l'anglais n'est pas donné). */

const DISMISS_KEY = "generex:licence-message-masque";

export function messageText(message: LicenceMessage): string {
  return lang() === "en" && message.text_en ? message.text_en : message.text_fr;
}

/** Empreinte d'un message, pour qu'un nouveau message revienne même après la croix. */
export function messageStamp(message: LicenceMessage): string {
  return `${message.at ?? ""}|${message.text_fr}`;
}

function readDismissed(): string | null {
  try {
    return window.sessionStorage.getItem(DISMISS_KEY);
  } catch {
    return null;
  }
}

function writeDismissed(stamp: string): void {
  try {
    window.sessionStorage.setItem(DISMISS_KEY, stamp);
  } catch {
    // Stockage refusé : le bandeau se ferme pour cette page seulement.
  }
}

export function LicenceMessageBanner({ message, onDismiss }: { message: LicenceMessage; onDismiss?: () => void }) {
  return (
    <div role="status" aria-live="polite" data-licence-message className="flex items-start gap-2.5 border-b border-accent/20 bg-accent-soft py-2 pr-2 pl-3">
      <Megaphone size={15} aria-hidden className="mt-0.5 shrink-0 text-accent" />
      <p className="min-w-0 flex-1 py-px text-meta leading-snug break-words text-ink-soft">
        <span className="font-semibold text-ink">{t("Message de BELLICOM")}</span>
        <span className="ml-1.5 text-ink">{messageText(message)}</span>
        {message.until && <span className="ml-1.5 text-ink-muted">{t("(jusqu'au {date})", { date: formatDateTime(message.until) })}</span>}
      </p>
      {onDismiss && (
        <IconButton size="xs" className="-my-0.5 shrink-0" onClick={onDismiss} title={t("Masquer")} aria-label={t("Masquer")}>
          <X size={14} />
        </IconButton>
      )}
    </div>
  );
}

/** Bandeau lu dans l'état de la licence (même requête que le portail, sans lecture de plus). */
export function LicenceMessageHost() {
  const { data } = useQuery({ queryKey: LICENCE_QUERY, queryFn: api.licence, enabled: false });
  const message = data?.licensed ? (data.licence_message ?? null) : null;
  const [dismissed, setDismissed] = useState(readDismissed);
  const shown = Boolean(message && messageStamp(message) !== dismissed);
  const last = useLastDefined(shown ? message : null);
  // Première apparition : posé fermé, puis ouvert à l'image suivante (jamais d'un coup).
  const present = Boolean(last);
  const [entered, setEntered] = useState(false);
  useEffect(() => {
    if (!present) return;
    const frame = window.requestAnimationFrame(() => setEntered(true));
    return () => window.cancelAnimationFrame(frame);
  }, [present]);
  if (!last) return null;
  const dismiss = () => {
    if (!message) return;
    const stamp = messageStamp(message);
    writeDismissed(stamp);
    setDismissed(stamp);
  };
  return (
    <Disclosure open={shown && entered} className="relative z-[84] shrink-0">
      <LicenceMessageBanner message={last} onDismiss={dismiss} />
    </Disclosure>
  );
}
